import { useCallback, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { UploadFileOutlined } from "@mui/icons-material";
import { COLOR_CONSTANTS } from "../helpers/Colors";

/** Border grey is not in COLOR_CONSTANTS yet; everything else comes from there. */
const COLORS = {
  primary: COLOR_CONSTANTS["primary/default"],
  primaryHover: COLOR_CONSTANTS["on-primary/variant"],
  primaryBg: COLOR_CONSTANTS["primary/variant"],
  border: "#ccc",
  textSecondary: COLOR_CONSTANTS["on-surface/secondary"],
};

export interface DropZoneProps {
  onFilesSelected: (files: File[]) => void;
  /** File-type filter for the native picker, e.g. ".csv" or ".pdf,.png,.jpg,.jpeg" */
  accept: string;
  /** Main instruction text, e.g. "Drop CSV file here or click to browse" */
  title: string;
  /** Secondary description, e.g. "CSV · 1 file per upload" */
  subtitle: string;
  /** Accessible label for the hidden file input. Defaults to "Select file". */
  ariaLabel?: string;
  /** Label for the browse button. Defaults to "Browse Files". */
  buttonLabel?: string;
  /** Show a compact version (single-line link) when files are listed below. */
  compact?: boolean;
  disabled?: boolean;
  /** Whether multiple files can be selected/dropped. Defaults to true. */
  multiple?: boolean;
}

export default function DropZone({
  onFilesSelected,
  accept,
  title,
  subtitle,
  ariaLabel = "Select file",
  buttonLabel = "Browse Files",
  compact,
  disabled,
  multiple = true,
}: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragCount, setDragCount] = useState(0);
  const isDragging = dragCount > 0;

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const arr = Array.from(fileList);
      // When single-file mode, silently take only the first dropped file
      onFilesSelected(!multiple && arr.length > 1 ? [arr[0]] : arr);
    },
    [multiple, onFilesSelected],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset so the same file(s) can be re-selected
      e.target.value = "";
    },
    [handleFiles],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCount((c) => c + 1);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCount((c) => c - 1);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCount(0);
      if (!disabled) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, handleFiles],
  );

  const openFilePicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={openFilePicker}
      sx={{
        border: `2px dashed ${isDragging ? COLORS.primary : COLORS.border}`,
        borderRadius: "12px",
        backgroundColor: isDragging ? COLORS.primaryBg : "#FAFAFA",
        transition: "border-color 0.15s, background-color 0.15s",
        p: compact ? 1.5 : 5,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        ...(compact && { "&:hover": { borderColor: COLORS.primary } }),
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        hidden
        onChange={handleInputChange}
        aria-label={ariaLabel}
      />

      {compact ? (
        <Typography variant="body2" color={COLORS.primary} fontWeight={500}>
          + Add more files
        </Typography>
      ) : (
        <>
          <UploadFileOutlined sx={{ fontSize: 48, color: COLORS.border, mb: 1 }} />
          <Typography fontWeight={600} fontSize="14px" mb={0.5}>
            {title}
          </Typography>
          <Typography variant="body2" color={COLORS.textSecondary} mb={2}>
            {subtitle}
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              openFilePicker();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              height: 36,
              px: 3,
              boxShadow: "none",
              backgroundColor: COLORS.primary,
              "&:hover": { backgroundColor: COLORS.primaryHover },
            }}
          >
            {buttonLabel}
          </Button>
        </>
      )}
    </Box>
  );
}
